/**
 * P3 Services Domain Seed Data
 *
 * Contains seed data for P3 (lower priority) services:
 * - Report Generation Service (11): Report templates, scheduled reports
 * - Compliance Service (12): Compliance rules, audit checklists
 * - Workflow Orchestration Service (13): Workflow templates, automation rules
 * - Content Safety Service (14): Safety rules, moderation policies
 * - Feature Flags: Admin portal feature flag management
 * - Identity Verification: KYC, document, address, and fraud rules
 * - Consumer Reports: Consumer-facing report definitions
 *
 * These services have fewer seed data requirements but need basic data
 * for testing and demonstration purposes.
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// REPORT GENERATION SERVICE (11)
// =============================================================================

export const REPORT_TEMPLATE_IDS = {
  LOAN_PIPELINE_REPORT: seedId('report-loan-pipeline', 1),
  PORTFOLIO_SUMMARY_REPORT: seedId('report-portfolio-summary', 2),
  COMPLIANCE_AUDIT_REPORT: seedId('report-compliance-audit', 3),
  TEAM_PERFORMANCE_REPORT: seedId('report-team-performance', 4),
  REVENUE_FORECAST_REPORT: seedId('report-revenue-forecast', 5),
} as const;

export const SCHEDULED_REPORT_IDS = {
  DAILY_PIPELINE_SUMMARY: seedId('scheduled-daily-pipeline', 1),
  WEEKLY_PORTFOLIO_REVIEW: seedId('scheduled-weekly-portfolio', 2),
  MONTHLY_COMPLIANCE_REPORT: seedId('scheduled-monthly-compliance', 3),
} as const;

export interface ReportTemplateSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: string;
  format: 'PDF' | 'EXCEL' | 'CSV' | 'HTML';
  dataSource: string;
  filters?: Record<string, unknown>;
  columns: Array<{ field: string; label: string; width?: number }>;
  groupBy?: string[];
  sortBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  isActive: boolean;
  createdBy: string;
}

export interface ScheduledReportSeedData {
  id: string;
  businessId: string;
  templateId: string;
  name: string;
  schedule: string; // cron expression
  recipients: string[];
  format: 'PDF' | 'EXCEL' | 'CSV';
  isActive: boolean;
  createdBy: string;
}

export const REPORT_TEMPLATES: Record<keyof typeof REPORT_TEMPLATE_IDS, ReportTemplateSeedData> = {
  LOAN_PIPELINE_REPORT: {
    id: REPORT_TEMPLATE_IDS.LOAN_PIPELINE_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Pipeline Report',
    description: 'Current status of all loans in the pipeline',
    category: 'operations',
    format: 'PDF',
    dataSource: 'loans',
    columns: [
      { field: 'loan_number', label: 'Loan #', width: 100 },
      { field: 'borrower_name', label: 'Borrower', width: 150 },
      { field: 'loan_amount', label: 'Amount', width: 100 },
      { field: 'status', label: 'Status', width: 100 },
      { field: 'stage', label: 'Stage', width: 120 },
      { field: 'assigned_to', label: 'Assigned To', width: 120 },
    ],
    groupBy: ['status'],
    sortBy: [{ field: 'loan_amount', direction: 'desc' }],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  PORTFOLIO_SUMMARY_REPORT: {
    id: REPORT_TEMPLATE_IDS.PORTFOLIO_SUMMARY_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Portfolio Summary Report',
    description: 'Summary of all active loans in the portfolio',
    category: 'executive',
    format: 'EXCEL',
    dataSource: 'portfolio',
    columns: [
      { field: 'loan_type', label: 'Loan Type' },
      { field: 'total_count', label: 'Count' },
      { field: 'total_amount', label: 'Total Amount' },
      { field: 'avg_rate', label: 'Avg Rate' },
      { field: 'avg_ltv', label: 'Avg LTV' },
    ],
    groupBy: ['loan_type'],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  COMPLIANCE_AUDIT_REPORT: {
    id: REPORT_TEMPLATE_IDS.COMPLIANCE_AUDIT_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Compliance Audit Report',
    description: 'Compliance check results and audit trail',
    category: 'compliance',
    format: 'PDF',
    dataSource: 'compliance_checks',
    columns: [
      { field: 'entity_type', label: 'Entity' },
      { field: 'check_name', label: 'Check' },
      { field: 'status', label: 'Status' },
      { field: 'checked_at', label: 'Date' },
      { field: 'checked_by', label: 'By' },
    ],
    sortBy: [{ field: 'checked_at', direction: 'desc' }],
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  TEAM_PERFORMANCE_REPORT: {
    id: REPORT_TEMPLATE_IDS.TEAM_PERFORMANCE_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Team Performance Report',
    description: 'Performance metrics for team members',
    category: 'operations',
    format: 'EXCEL',
    dataSource: 'team_metrics',
    columns: [
      { field: 'user_name', label: 'Team Member' },
      { field: 'loans_processed', label: 'Loans Processed' },
      { field: 'avg_processing_time', label: 'Avg Time (days)' },
      { field: 'sla_compliance', label: 'SLA %' },
      { field: 'rating', label: 'Rating' },
    ],
    sortBy: [{ field: 'loans_processed', direction: 'desc' }],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  REVENUE_FORECAST_REPORT: {
    id: REPORT_TEMPLATE_IDS.REVENUE_FORECAST_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Revenue Forecast Report',
    description: 'Projected revenue based on pipeline',
    category: 'finance',
    format: 'PDF',
    dataSource: 'revenue_forecast',
    columns: [
      { field: 'month', label: 'Month' },
      { field: 'projected_fundings', label: 'Projected Fundings' },
      { field: 'projected_revenue', label: 'Projected Revenue' },
      { field: 'confidence', label: 'Confidence %' },
    ],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

export const SCHEDULED_REPORTS: Record<keyof typeof SCHEDULED_REPORT_IDS, ScheduledReportSeedData> = {
  DAILY_PIPELINE_SUMMARY: {
    id: SCHEDULED_REPORT_IDS.DAILY_PIPELINE_SUMMARY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: REPORT_TEMPLATE_IDS.LOAN_PIPELINE_REPORT,
    name: 'Daily Pipeline Summary',
    schedule: '0 8 * * *', // 8 AM daily
    recipients: ['rachel.martinez@opsmatrix.local', 'mike.chen@opsmatrix.local'],
    format: 'PDF',
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  WEEKLY_PORTFOLIO_REVIEW: {
    id: SCHEDULED_REPORT_IDS.WEEKLY_PORTFOLIO_REVIEW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: REPORT_TEMPLATE_IDS.PORTFOLIO_SUMMARY_REPORT,
    name: 'Weekly Portfolio Review',
    schedule: '0 9 * * 1', // Monday 9 AM
    recipients: ['rachel.martinez@opsmatrix.local'],
    format: 'EXCEL',
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  MONTHLY_COMPLIANCE_REPORT: {
    id: SCHEDULED_REPORT_IDS.MONTHLY_COMPLIANCE_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: REPORT_TEMPLATE_IDS.COMPLIANCE_AUDIT_REPORT,
    name: 'Monthly Compliance Report',
    schedule: '0 6 1 * *', // 1st of month 6 AM
    recipients: ['compliance@opsmatrix.local', 'rachel.martinez@opsmatrix.local'],
    format: 'PDF',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// COMPLIANCE SERVICE (12)
// =============================================================================

export const COMPLIANCE_RULE_IDS = {
  NMLS_LICENSE_CHECK: seedId('compliance-nmls-check', 1),
  STATE_LICENSE_CHECK: seedId('compliance-state-check', 2),
  AML_KYC_CHECK: seedId('compliance-aml-kyc', 3),
  DOCUMENT_RETENTION: seedId('compliance-doc-retention', 4),
  DATA_PRIVACY_CHECK: seedId('compliance-data-privacy', 5),
} as const;

export const AUDIT_CHECKLIST_IDS = {
  LOAN_ORIGINATION_CHECKLIST: seedId('audit-loan-orig', 1),
  MONTHLY_REVIEW_CHECKLIST: seedId('audit-monthly-review', 2),
  ANNUAL_COMPLIANCE_CHECKLIST: seedId('audit-annual-compliance', 3),
} as const;

export interface ComplianceRuleSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: string;
  entityType: string;
  conditions: Array<{ field: string; operator: string; value: unknown }>;
  severity: 'critical' | 'high' | 'medium' | 'low';
  autoRemediation: boolean;
  remediationAction?: string;
  notifyOnViolation: boolean;
  isActive: boolean;
  createdBy: string;
}

export interface AuditChecklistSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: string;
  frequency: 'transaction' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
  items: Array<{
    name: string;
    description: string;
    required: boolean;
    evidenceRequired: boolean;
  }>;
  isActive: boolean;
  createdBy: string;
}

export const COMPLIANCE_RULES: Record<keyof typeof COMPLIANCE_RULE_IDS, ComplianceRuleSeedData> = {
  NMLS_LICENSE_CHECK: {
    id: COMPLIANCE_RULE_IDS.NMLS_LICENSE_CHECK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'NMLS License Verification',
    description: 'Verify all loan officers have valid NMLS licenses',
    category: 'licensing',
    entityType: 'user',
    conditions: [
      { field: 'role', operator: 'in', value: ['loan_officer', 'processor', 'underwriter'] },
      { field: 'nmls_status', operator: 'eq', value: 'active' },
    ],
    severity: 'critical',
    autoRemediation: false,
    notifyOnViolation: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  STATE_LICENSE_CHECK: {
    id: COMPLIANCE_RULE_IDS.STATE_LICENSE_CHECK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'State License Verification',
    description: 'Verify business is licensed in loan property state',
    category: 'licensing',
    entityType: 'loan_application',
    conditions: [{ field: 'property_state', operator: 'in_licensed_states', value: true }],
    severity: 'critical',
    autoRemediation: false,
    notifyOnViolation: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  AML_KYC_CHECK: {
    id: COMPLIANCE_RULE_IDS.AML_KYC_CHECK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'AML/KYC Verification',
    description: 'Verify borrower identity and AML screening',
    category: 'aml',
    entityType: 'consumer',
    conditions: [
      { field: 'kyc_verified', operator: 'eq', value: true },
      { field: 'aml_cleared', operator: 'eq', value: true },
    ],
    severity: 'critical',
    autoRemediation: false,
    notifyOnViolation: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DOCUMENT_RETENTION: {
    id: COMPLIANCE_RULE_IDS.DOCUMENT_RETENTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Retention Policy',
    description: 'Ensure documents are retained per regulatory requirements',
    category: 'records',
    entityType: 'document',
    conditions: [{ field: 'retention_period_years', operator: 'gte', value: 7 }],
    severity: 'high',
    autoRemediation: true,
    remediationAction: 'extend_retention',
    notifyOnViolation: false,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DATA_PRIVACY_CHECK: {
    id: COMPLIANCE_RULE_IDS.DATA_PRIVACY_CHECK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Data Privacy Compliance',
    description: 'Verify PII handling meets privacy requirements',
    category: 'privacy',
    entityType: 'data_access',
    conditions: [
      { field: 'pii_encrypted', operator: 'eq', value: true },
      { field: 'access_logged', operator: 'eq', value: true },
    ],
    severity: 'high',
    autoRemediation: false,
    notifyOnViolation: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

export const AUDIT_CHECKLISTS: Record<keyof typeof AUDIT_CHECKLIST_IDS, AuditChecklistSeedData> = {
  LOAN_ORIGINATION_CHECKLIST: {
    id: AUDIT_CHECKLIST_IDS.LOAN_ORIGINATION_CHECKLIST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Origination Audit Checklist',
    description: 'Checklist for each loan origination',
    category: 'origination',
    frequency: 'transaction',
    items: [
      { name: 'Borrower ID Verified', description: 'Government ID verified', required: true, evidenceRequired: true },
      {
        name: 'Income Documented',
        description: 'Income documentation collected',
        required: true,
        evidenceRequired: true,
      },
      { name: 'Property Appraisal', description: 'Appraisal completed', required: true, evidenceRequired: true },
      { name: 'Title Search', description: 'Title search completed', required: true, evidenceRequired: true },
      {
        name: 'Disclosures Provided',
        description: 'All required disclosures provided',
        required: true,
        evidenceRequired: true,
      },
    ],
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MONTHLY_REVIEW_CHECKLIST: {
    id: AUDIT_CHECKLIST_IDS.MONTHLY_REVIEW_CHECKLIST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Monthly Review Checklist',
    description: 'Monthly compliance review tasks',
    category: 'periodic',
    frequency: 'monthly',
    items: [
      {
        name: 'License Status Check',
        description: 'Verify all licenses are current',
        required: true,
        evidenceRequired: false,
      },
      {
        name: 'Portfolio Review',
        description: 'Review portfolio for compliance issues',
        required: true,
        evidenceRequired: false,
      },
      {
        name: 'Complaint Review',
        description: 'Review and address any complaints',
        required: true,
        evidenceRequired: false,
      },
      { name: 'Training Records', description: 'Verify training completion', required: false, evidenceRequired: false },
    ],
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  ANNUAL_COMPLIANCE_CHECKLIST: {
    id: AUDIT_CHECKLIST_IDS.ANNUAL_COMPLIANCE_CHECKLIST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Annual Compliance Audit',
    description: 'Comprehensive annual compliance audit',
    category: 'annual',
    frequency: 'annual',
    items: [
      { name: 'Policy Review', description: 'Review and update all policies', required: true, evidenceRequired: true },
      {
        name: 'Risk Assessment',
        description: 'Complete annual risk assessment',
        required: true,
        evidenceRequired: true,
      },
      { name: 'Vendor Audits', description: 'Audit all critical vendors', required: true, evidenceRequired: true },
      {
        name: 'Security Assessment',
        description: 'Complete security assessment',
        required: true,
        evidenceRequired: true,
      },
      {
        name: 'Disaster Recovery Test',
        description: 'Test disaster recovery plan',
        required: true,
        evidenceRequired: true,
      },
    ],
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// WORKFLOW ORCHESTRATION SERVICE (13)
// =============================================================================

export const WORKFLOW_TEMPLATE_IDS = {
  LOAN_APPROVAL_WORKFLOW: seedId('workflow-loan-approval', 1),
  DOCUMENT_REVIEW_WORKFLOW: seedId('workflow-doc-review', 2),
  ONBOARDING_WORKFLOW: seedId('workflow-onboarding', 3),
  ESCALATION_WORKFLOW: seedId('workflow-escalation', 4),
} as const;

export const AUTOMATION_RULE_IDS = {
  AUTO_ASSIGN_LOAN: seedId('automation-auto-assign', 1),
  AUTO_NOTIFY_SLA: seedId('automation-notify-sla', 2),
  AUTO_ARCHIVE_COMPLETED: seedId('automation-archive', 3),
} as const;

export interface WorkflowTemplateSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  entityType: string;
  triggerEvent: string;
  steps: Array<{
    name: string;
    type: 'action' | 'decision' | 'wait' | 'notification';
    config: Record<string, unknown>;
    nextStep?: string;
  }>;
  isActive: boolean;
  createdBy: string;
}

export interface AutomationRuleSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  triggerEvent: string;
  conditions: Array<{ field: string; operator: string; value: unknown }>;
  actions: Array<{ type: string; config: Record<string, unknown> }>;
  isActive: boolean;
  priority: number;
  createdBy: string;
}

export const WORKFLOW_TEMPLATES: Record<keyof typeof WORKFLOW_TEMPLATE_IDS, WorkflowTemplateSeedData> = {
  LOAN_APPROVAL_WORKFLOW: {
    id: WORKFLOW_TEMPLATE_IDS.LOAN_APPROVAL_WORKFLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Approval Workflow',
    description: 'Standard loan approval process',
    entityType: 'loan_application',
    triggerEvent: 'loan.submitted',
    steps: [
      { name: 'Initial Review', type: 'action', config: { assignTo: 'processor' }, nextStep: 'underwriting' },
      { name: 'Underwriting', type: 'action', config: { assignTo: 'underwriter' }, nextStep: 'decision' },
      {
        name: 'Approval Decision',
        type: 'decision',
        config: { field: 'recommendation', approved: 'notify_approved', rejected: 'notify_rejected' },
      },
      { name: 'Notify Approved', type: 'notification', config: { template: 'loan_approved' } },
      { name: 'Notify Rejected', type: 'notification', config: { template: 'loan_rejected' } },
    ],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  DOCUMENT_REVIEW_WORKFLOW: {
    id: WORKFLOW_TEMPLATE_IDS.DOCUMENT_REVIEW_WORKFLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Review Workflow',
    description: 'Workflow for reviewing uploaded documents',
    entityType: 'document',
    triggerEvent: 'document.uploaded',
    steps: [
      { name: 'OCR Processing', type: 'action', config: { action: 'process_ocr' }, nextStep: 'ai_review' },
      { name: 'AI Review', type: 'action', config: { action: 'ai_classify' }, nextStep: 'quality_check' },
      {
        name: 'Quality Check',
        type: 'decision',
        config: { field: 'quality_score', pass: 'approve', fail: 'manual_review' },
      },
      { name: 'Manual Review', type: 'action', config: { assignTo: 'processor' }, nextStep: 'approve' },
      { name: 'Approve Document', type: 'action', config: { action: 'mark_approved' } },
    ],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  ONBOARDING_WORKFLOW: {
    id: WORKFLOW_TEMPLATE_IDS.ONBOARDING_WORKFLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'User Onboarding Workflow',
    description: 'Workflow for new user onboarding',
    entityType: 'user',
    triggerEvent: 'user.created',
    steps: [
      { name: 'Send Welcome Email', type: 'notification', config: { template: 'welcome' }, nextStep: 'setup_profile' },
      {
        name: 'Setup Profile',
        type: 'wait',
        config: { waitFor: 'profile.completed', timeout: '7d' },
        nextStep: 'training',
      },
      { name: 'Assign Training', type: 'action', config: { action: 'assign_training' }, nextStep: 'complete' },
      { name: 'Complete', type: 'action', config: { action: 'mark_onboarded' } },
    ],
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  ESCALATION_WORKFLOW: {
    id: WORKFLOW_TEMPLATE_IDS.ESCALATION_WORKFLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Escalation Workflow',
    description: 'SLA breach escalation workflow',
    entityType: 'case',
    triggerEvent: 'sla.breached',
    steps: [
      {
        name: 'Notify Assignee',
        type: 'notification',
        config: { recipients: ['assignee'] },
        nextStep: 'wait_response',
      },
      {
        name: 'Wait for Response',
        type: 'wait',
        config: { waitFor: 'response', timeout: '2h' },
        nextStep: 'escalate_manager',
      },
      { name: 'Escalate to Manager', type: 'action', config: { action: 'escalate', to: 'manager' } },
    ],
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

export const AUTOMATION_RULES: Record<keyof typeof AUTOMATION_RULE_IDS, AutomationRuleSeedData> = {
  AUTO_ASSIGN_LOAN: {
    id: AUTOMATION_RULE_IDS.AUTO_ASSIGN_LOAN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Auto-Assign Loan Applications',
    description: 'Automatically assign new loans to available processors',
    triggerEvent: 'loan.created',
    conditions: [
      { field: 'status', operator: 'eq', value: 'submitted' },
      { field: 'assignee_id', operator: 'is_null', value: true },
    ],
    actions: [{ type: 'round_robin_assign', config: { role: 'processor', teamId: 'processing-team' } }],
    isActive: true,
    priority: 10,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  AUTO_NOTIFY_SLA: {
    id: AUTOMATION_RULE_IDS.AUTO_NOTIFY_SLA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'SLA Warning Notification',
    description: 'Notify when SLA is approaching deadline',
    triggerEvent: 'sla.warning',
    conditions: [{ field: 'time_remaining', operator: 'lte', value: '2h' }],
    actions: [{ type: 'send_notification', config: { template: 'sla_warning', channels: ['email', 'push'] } }],
    isActive: true,
    priority: 20,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  AUTO_ARCHIVE_COMPLETED: {
    id: AUTOMATION_RULE_IDS.AUTO_ARCHIVE_COMPLETED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Auto-Archive Completed Loans',
    description: 'Archive loans 90 days after funding',
    triggerEvent: 'loan.funded',
    conditions: [{ field: 'funded_days_ago', operator: 'gte', value: 90 }],
    actions: [{ type: 'update_field', config: { field: 'status', value: 'archived' } }],
    isActive: true,
    priority: 5,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// CONTENT SAFETY SERVICE (14)
// =============================================================================

export const SAFETY_RULE_IDS = {
  PII_DETECTION: seedId('safety-pii-detect', 1),
  PROFANITY_FILTER: seedId('safety-profanity', 2),
  SPAM_DETECTION: seedId('safety-spam', 3),
  MALICIOUS_CONTENT: seedId('safety-malicious', 4),
} as const;

export const MODERATION_POLICY_IDS = {
  DEFAULT_POLICY: seedId('moderation-default', 1),
  STRICT_POLICY: seedId('moderation-strict', 2),
} as const;

export interface SafetyRuleSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: 'pii' | 'profanity' | 'spam' | 'malware' | 'harmful';
  contentTypes: string[];
  patterns?: string[];
  aiModelId?: string;
  action: 'block' | 'flag' | 'redact' | 'warn';
  severity: 'critical' | 'high' | 'medium' | 'low';
  isActive: boolean;
  createdBy: string;
}

export interface ModerationPolicySeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  ruleIds: string[];
  autoModerate: boolean;
  requireManualReview: boolean;
  notifyOnViolation: boolean;
  escalationThreshold: number;
  isActive: boolean;
  createdBy: string;
}

export const SAFETY_RULES: Record<keyof typeof SAFETY_RULE_IDS, SafetyRuleSeedData> = {
  PII_DETECTION: {
    id: SAFETY_RULE_IDS.PII_DETECTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'PII Detection',
    description: 'Detect and redact PII from user content',
    category: 'pii',
    contentTypes: ['text', 'document', 'message'],
    patterns: ['\\b\\d{3}-\\d{2}-\\d{4}\\b', '\\b\\d{9}\\b'], // SSN patterns
    action: 'redact',
    severity: 'high',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROFANITY_FILTER: {
    id: SAFETY_RULE_IDS.PROFANITY_FILTER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Profanity Filter',
    description: 'Filter profanity from user communications',
    category: 'profanity',
    contentTypes: ['message', 'comment', 'note'],
    action: 'flag',
    severity: 'medium',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  SPAM_DETECTION: {
    id: SAFETY_RULE_IDS.SPAM_DETECTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Spam Detection',
    description: 'Detect and block spam content',
    category: 'spam',
    contentTypes: ['message', 'email', 'comment'],
    aiModelId: 'spam-classifier-v1',
    action: 'block',
    severity: 'medium',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MALICIOUS_CONTENT: {
    id: SAFETY_RULE_IDS.MALICIOUS_CONTENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Malicious Content Detection',
    description: 'Detect malicious links and attachments',
    category: 'malware',
    contentTypes: ['document', 'attachment', 'link'],
    action: 'block',
    severity: 'critical',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

export const MODERATION_POLICIES: Record<keyof typeof MODERATION_POLICY_IDS, ModerationPolicySeedData> = {
  DEFAULT_POLICY: {
    id: MODERATION_POLICY_IDS.DEFAULT_POLICY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Default Moderation Policy',
    description: 'Standard content moderation policy',
    ruleIds: [
      SAFETY_RULE_IDS.PII_DETECTION,
      SAFETY_RULE_IDS.PROFANITY_FILTER,
      SAFETY_RULE_IDS.SPAM_DETECTION,
      SAFETY_RULE_IDS.MALICIOUS_CONTENT,
    ],
    autoModerate: true,
    requireManualReview: false,
    notifyOnViolation: true,
    escalationThreshold: 3,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  STRICT_POLICY: {
    id: MODERATION_POLICY_IDS.STRICT_POLICY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Strict Moderation Policy',
    description: 'Strict content moderation for external communications',
    ruleIds: [
      SAFETY_RULE_IDS.PII_DETECTION,
      SAFETY_RULE_IDS.PROFANITY_FILTER,
      SAFETY_RULE_IDS.SPAM_DETECTION,
      SAFETY_RULE_IDS.MALICIOUS_CONTENT,
    ],
    autoModerate: true,
    requireManualReview: true,
    notifyOnViolation: true,
    escalationThreshold: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// FEATURE FLAG SEED DATA (for Admin Portal feature-flags page)
// =============================================================================

export const FEATURE_FLAG_IDS = {
  AI_PROSPECT_DISCOVERY: seedId('ff-ai-prospect', 1),
  VOICE_CAMPAIGNS: seedId('ff-voice-campaigns', 2),
  ADVANCED_ANALYTICS: seedId('ff-advanced-analytics', 3),
  ENTITY_IMPORT_EXPORT: seedId('ff-entity-import', 4),
  CONSUMER_SELF_SERVICE: seedId('ff-consumer-self-svc', 5),
  DARK_MODE: seedId('ff-dark-mode', 6),
  // Issue #1304 — gates the Term Sheet E-Signature MVP (tickets #1305–#1309).
  // Defaults to OFF so schema changes ship neutral; portals flip this when all 6 tickets land.
  ENABLE_TERMSHEET_SIGNING_V2: seedId('ff-termsheet-signing-v2', 7),
} as const;

export interface FeatureFlagSeedData {
  id: string;
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  rolloutPercentage: number;
  targetEnvironments: string[];
  createdBy: string;
}

export const FEATURE_FLAGS: Record<keyof typeof FEATURE_FLAG_IDS, FeatureFlagSeedData> = {
  AI_PROSPECT_DISCOVERY: {
    id: FEATURE_FLAG_IDS.AI_PROSPECT_DISCOVERY,
    key: 'ai_prospect_discovery',
    name: 'AI Prospect Discovery',
    description: 'Enable AI-powered prospect discovery using SFR Analytics integration',
    enabled: true,
    rolloutPercentage: 100,
    targetEnvironments: ['staging', 'production'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  VOICE_CAMPAIGNS: {
    id: FEATURE_FLAG_IDS.VOICE_CAMPAIGNS,
    key: 'voice_campaigns',
    name: 'Voice Campaigns (Retell AI)',
    description: 'Enable AI-powered voice campaign outreach via Retell integration',
    enabled: false,
    rolloutPercentage: 0,
    targetEnvironments: ['staging'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  ADVANCED_ANALYTICS: {
    id: FEATURE_FLAG_IDS.ADVANCED_ANALYTICS,
    key: 'advanced_analytics',
    name: 'Advanced Analytics Dashboard',
    description: 'Enable enhanced analytics with predictive insights and custom reports',
    enabled: true,
    rolloutPercentage: 50,
    targetEnvironments: ['staging', 'production'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  ENTITY_IMPORT_EXPORT: {
    id: FEATURE_FLAG_IDS.ENTITY_IMPORT_EXPORT,
    key: 'entity_import_export',
    name: 'Entity Import/Export',
    description: 'Enable CSV/Excel import and export for custom entity records',
    enabled: true,
    rolloutPercentage: 100,
    targetEnvironments: ['staging', 'production'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  CONSUMER_SELF_SERVICE: {
    id: FEATURE_FLAG_IDS.CONSUMER_SELF_SERVICE,
    key: 'consumer_self_service',
    name: 'Consumer Self-Service Portal',
    description: 'Enable self-service features in consumer portal (document upload, case submission)',
    enabled: true,
    rolloutPercentage: 100,
    targetEnvironments: ['staging', 'production'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  DARK_MODE: {
    id: FEATURE_FLAG_IDS.DARK_MODE,
    key: 'dark_mode',
    name: 'Dark Mode Theme',
    description: 'Enable dark mode theme option across all portals',
    enabled: true,
    rolloutPercentage: 100,
    targetEnvironments: ['staging', 'production'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  ENABLE_TERMSHEET_SIGNING_V2: {
    id: FEATURE_FLAG_IDS.ENABLE_TERMSHEET_SIGNING_V2,
    key: 'enable_termsheet_signing_v2',
    name: 'Term Sheet E-Signature v2',
    description:
      'Enable multi-signer term sheet workflow: admin template builder with signerPolicy, business portal Term Sheets tab, consumer portal "Documents to sign" list. Gates #1305–#1309.',
    enabled: false,
    rolloutPercentage: 0,
    targetEnvironments: ['staging'],
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// IDENTITY VERIFICATION SEED DATA (for Admin Portal identity page)
// =============================================================================

export const IDENTITY_RULE_IDS = {
  KYC_BASIC: seedId('identity-kyc-basic', 1),
  KYC_ENHANCED: seedId('identity-kyc-enhanced', 2),
  DOCUMENT_VERIFICATION: seedId('identity-doc-verify', 3),
  ADDRESS_VERIFICATION: seedId('identity-addr-verify', 4),
  FRAUD_DETECTION: seedId('identity-fraud-detect', 5),
} as const;

export interface IdentityRuleSeedData {
  id: string;
  name: string;
  description: string;
  type: 'kyc' | 'document' | 'address' | 'fraud' | 'biometric';
  severity: 'critical' | 'high' | 'medium' | 'low';
  isActive: boolean;
  config: Record<string, unknown>;
}

export const IDENTITY_RULES: Record<keyof typeof IDENTITY_RULE_IDS, IdentityRuleSeedData> = {
  KYC_BASIC: {
    id: IDENTITY_RULE_IDS.KYC_BASIC,
    name: 'Basic KYC Verification',
    description: 'Standard identity verification: name, date of birth, SSN last 4',
    type: 'kyc',
    severity: 'critical',
    isActive: true,
    config: { requiredFields: ['fullName', 'dateOfBirth', 'ssnLast4'], maxAttempts: 3 },
  },
  KYC_ENHANCED: {
    id: IDENTITY_RULE_IDS.KYC_ENHANCED,
    name: 'Enhanced KYC Verification',
    description: 'Full KYC with document upload and selfie verification for high-value loans',
    type: 'kyc',
    severity: 'critical',
    isActive: true,
    config: { loanAmountThreshold: 500000, requiresSelfie: true, requiresDocumentUpload: true },
  },
  DOCUMENT_VERIFICATION: {
    id: IDENTITY_RULE_IDS.DOCUMENT_VERIFICATION,
    name: 'Document Authenticity Check',
    description: 'Verify uploaded ID documents using OCR and database checks',
    type: 'document',
    severity: 'high',
    isActive: true,
    config: { acceptedDocuments: ['drivers_license', 'passport', 'state_id'], ocrEnabled: true },
  },
  ADDRESS_VERIFICATION: {
    id: IDENTITY_RULE_IDS.ADDRESS_VERIFICATION,
    name: 'Address Verification',
    description: 'Verify residential address against USPS and credit bureau records',
    type: 'address',
    severity: 'medium',
    isActive: true,
    config: { sources: ['usps', 'credit_bureau'], maxAgeDays: 90 },
  },
  FRAUD_DETECTION: {
    id: IDENTITY_RULE_IDS.FRAUD_DETECTION,
    name: 'Fraud Risk Scoring',
    description: 'Score applicants for fraud risk based on application patterns and data anomalies',
    type: 'fraud',
    severity: 'critical',
    isActive: true,
    config: {
      riskThreshold: 0.7,
      autoRejectThreshold: 0.9,
      factors: ['velocity', 'device', 'ip', 'data_consistency'],
    },
  },
};

// =============================================================================
// CONSUMER REPORT SEED DATA
// =============================================================================

export const CONSUMER_REPORT_IDS = {
  MY_LOAN_SUMMARY: seedId('consumer-report-loan-summary', 1),
  MY_PAYMENT_HISTORY: seedId('consumer-report-payments', 2),
  MY_DOCUMENT_STATUS: seedId('consumer-report-docs', 3),
} as const;

export interface ConsumerReportSeedData {
  id: string;
  name: string;
  description: string;
  category: string;
  format: 'PDF' | 'EXCEL';
  isActive: boolean;
}

export const CONSUMER_REPORTS: Record<keyof typeof CONSUMER_REPORT_IDS, ConsumerReportSeedData> = {
  MY_LOAN_SUMMARY: {
    id: CONSUMER_REPORT_IDS.MY_LOAN_SUMMARY,
    name: 'My Loan Summary',
    description: 'Overview of all your loan applications with current status',
    category: 'personal',
    format: 'PDF',
    isActive: true,
  },
  MY_PAYMENT_HISTORY: {
    id: CONSUMER_REPORT_IDS.MY_PAYMENT_HISTORY,
    name: 'Payment History',
    description: 'Complete payment history across all your loans',
    category: 'financial',
    format: 'PDF',
    isActive: true,
  },
  MY_DOCUMENT_STATUS: {
    id: CONSUMER_REPORT_IDS.MY_DOCUMENT_STATUS,
    name: 'Document Status Report',
    description: 'Status of all uploaded documents and pending requirements',
    category: 'documents',
    format: 'PDF',
    isActive: true,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Report Generation
export function getAllReportTemplates(): ReportTemplateSeedData[] {
  return Object.values(REPORT_TEMPLATES);
}

export function getAllScheduledReports(): ScheduledReportSeedData[] {
  return Object.values(SCHEDULED_REPORTS);
}

// Compliance
export function getAllComplianceRules(): ComplianceRuleSeedData[] {
  return Object.values(COMPLIANCE_RULES);
}

export function getAllAuditChecklists(): AuditChecklistSeedData[] {
  return Object.values(AUDIT_CHECKLISTS);
}

// Workflow
export function getAllWorkflowTemplates(): WorkflowTemplateSeedData[] {
  return Object.values(WORKFLOW_TEMPLATES);
}

export function getAllAutomationRules(): AutomationRuleSeedData[] {
  return Object.values(AUTOMATION_RULES);
}

// Content Safety
export function getAllSafetyRules(): SafetyRuleSeedData[] {
  return Object.values(SAFETY_RULES);
}

export function getAllModerationPolicies(): ModerationPolicySeedData[] {
  return Object.values(MODERATION_POLICIES);
}

// Feature Flags
export function getAllFeatureFlags(): FeatureFlagSeedData[] {
  return Object.values(FEATURE_FLAGS);
}

export function getEnabledFeatureFlags(): FeatureFlagSeedData[] {
  return Object.values(FEATURE_FLAGS).filter((f) => f.enabled);
}

// Identity Verification
export function getAllIdentityRules(): IdentityRuleSeedData[] {
  return Object.values(IDENTITY_RULES);
}

export function getActiveIdentityRules(): IdentityRuleSeedData[] {
  return Object.values(IDENTITY_RULES).filter((r) => r.isActive);
}

// Consumer Reports
export function getAllConsumerReports(): ConsumerReportSeedData[] {
  return Object.values(CONSUMER_REPORTS);
}

export function getActiveConsumerReports(): ConsumerReportSeedData[] {
  return Object.values(CONSUMER_REPORTS).filter((r) => r.isActive);
}
